import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/app/Common";
import { Logo, AQMark } from "@/components/app/Logo";
import { HealthGauge } from "@/components/app/HealthGauge";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, signIn } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/app" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(isLogin ? "/auth/login" : "/auth/register", isLogin ? { email: f.email, password: f.password } : f);
      signIn(r.data);
      nav(params.get("plan") ? "/app/billing" : "/app");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <Link to="/"><Logo tagline /></Link>
        <div className="max-w-sm w-full mt-12 rise">
          <h1 className="text-3xl font-bold tracking-tight" data-testid="auth-title">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-muted-foreground mt-2">{isLogin ? "Sign in to your corporate command center." : "14-day Professional trial. No card required."}</p>
          <Button variant="outline" className="w-full mt-8" onClick={googleLogin} data-testid="google-login-btn">
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.9-5.5 3.9-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.3 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z" /></svg>
            Continue with Google
          </Button>
          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />or<div className="h-px flex-1 bg-border" /></div>
          <form onSubmit={submit} className="space-y-4">
            {!isLogin && <Field label="Full name"><Input required value={f.name} onChange={set("name")} data-testid="auth-name-input" /></Field>}
            <Field label="Email"><Input required type="email" value={f.email} onChange={set("email")} data-testid="auth-email-input" /></Field>
            <Field label="Password"><Input required type="password" minLength={isLogin ? 1 : 8} value={f.password} onChange={set("password")} data-testid="auth-password-input" /></Field>
            <Button type="submit" className="w-full" disabled={busy} data-testid="auth-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isLogin ? "Sign in" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6">
            {isLogin ? "New to AccruallQ? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-primary font-medium" data-testid="auth-switch-link">{isLogin ? "Create an account" : "Sign in"}</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex relative bg-[#0B0F19] items-center justify-center overflow-hidden noise">
        <img src="/img/hero-tower.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative text-center text-slate-100 px-12">
          <AQMark size={72} testId="auth-mark" className="mx-auto mb-8 shadow-[0_0_60px_-10px_rgba(16,185,129,.55)] rounded-[18px]" />
          <HealthGauge score={91} size={260} testId="auth-gauge" />
          <p className="mt-8 text-lg font-display max-w-sm mx-auto">Know the health of every corporation you own — before the registry does.</p>
        </div>
      </div>
    </div>
  );
}
